import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { DialogService } from 'src/app/shared/utils/dialog/dialog.service';

/**
 * The core-api's answer when a save came from an outdated copy: someone else saved the same
 * record in between (optimistic locking, persistence.optimisticLocking=enforce).
 */
export function isStaleWrite(error: unknown): boolean {
  if (
    !(error instanceof HttpErrorResponse) ||
    error.status !== HttpStatusCode.Conflict
  ) {
    return false;
  }
  let body = error.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return false;
    }
  }
  return body?.error === 'stale_write';
}

/**
 * Explains a stale write in a dialog with "reload" - a snackbar would be replaced at once by the
 * caller's own error message. The error still reaches the caller, so spinners and forms end as
 * on any failed save; several requests failing together show one dialog.
 */
@Injectable()
export class StaleWriteInterceptor implements HttpInterceptor {
  private dialogService = inject(DialogService);
  private dialogOpen = false;

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (isStaleWrite(error)) {
          this.explain();
        }
        return throwError(() => error);
      })
    );
  }

  private explain() {
    if (this.dialogOpen) {
      return;
    }
    this.dialogOpen = true;
    this.dialogService
      .openDialog({
        title: 'Inzwischen geändert',
        message:
          'Ihre Änderung wurde nicht gespeichert: Diese Daten wurden in der Zwischenzeit geändert, von jemand anderem oder in einem anderen Tab. Laden Sie die Seite neu und nehmen Sie die Änderung noch einmal vor.',
        confirmButton: 'Seite neu laden',
        cancelButton: 'Schließen',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((reload) => {
        this.dialogOpen = false;
        if (reload) {
          this.reload();
        }
      });
  }

  /** Seam for tests. */
  reload() {
    window.location.reload();
  }
}
